import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import helperService from '../services/helperService';
import { useToast } from '../contexts/ToastContext';
import './SetupHelper.css';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const TIME_BLOCKS = [
  { label: 'Morning',   sub: '6am – Noon'  },
  { label: 'Afternoon', sub: 'Noon – 6pm'  },
  { label: 'Evening',   sub: '6pm – 10pm'  },
  { label: 'Night',     sub: '10pm – 6am'  },
];

const initAvailability = () => {
  const a = {};
  DAYS.forEach(d => {
    a[d] = {};
    TIME_BLOCKS.forEach(({ label }) => { a[d][label] = false; });
  });
  return a;
};

/** Convert flat [{day, block}] slots back into the nested grid object. */
const slotsToAvailability = (slots) => {
  const grid = initAvailability();
  slots.forEach(({ day, block }) => {
    if (grid[day] && block in grid[day]) grid[day][block] = true;
  });
  return grid;
};

function SetupHelper() {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [equipmentCatalog, setEquipmentCatalog] = useState([]);  // [{id, label}]
  const [skillsCatalog, setSkillsCatalog]       = useState([]);  // [{id, label}]
  const [equipment, setEquipment]               = useState([]);  // selected IDs (number[])
  const [skills, setSkills]                     = useState([]);  // selected IDs (number[])
  const [availability, setAvailability]         = useState(initAvailability());
  const [available, setAvailable]               = useState(true);
  const [isSaving, setIsSaving]                 = useState(false);
  const [profileExists, setProfileExists]       = useState(false);
  const [catalogLoading, setCatalogLoading]     = useState(true);

  // Fetch catalogs and existing profile in parallel on mount.
  useEffect(() => {
    Promise.all([
      helperService.getEquipmentCatalog(),
      helperService.getSkillsCatalog(),
      helperService.getProfile(),
    ]).then(([eqCat, skCat, profile]) => {
      setEquipmentCatalog(eqCat);
      setSkillsCatalog(skCat);
      if (!profile) return;
      setProfileExists(true);
      if (profile.equipment?.length)    setEquipment(profile.equipment.map(e => e.id));
      if (profile.skills?.length)       setSkills(profile.skills.map(s => s.id));
      if (profile.availability?.length) setAvailability(slotsToAvailability(profile.availability));
    }).finally(() => setCatalogLoading(false));
  }, []);

  const toggleId = (setList, id) =>
    setList(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);

  const toggleAvail = (day, block) =>
    setAvailability(prev => ({
      ...prev,
      [day]: { ...prev[day], [block]: !prev[day][block] },
    }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      if (profileExists) {
        await helperService.updateProfile(equipment, skills, availability);
      } else {
        await helperService.saveProfile(equipment, skills, availability);
      }
      addToast('Helper profile saved!', 'success');
      navigate('/dashboard');
    } catch (err) {
      addToast(err.message || 'Could not save profile. Please try again.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="setup-helper-page">
      <div className="setup-helper-container">

        <div className="setup-helper-header">
          <div className="helper-header-text">
            <h1>Your Helper Profile</h1>
            <p>Let your neighbors know what you can offer in an emergency.</p>
          </div>
          <label className="availability-toggle" aria-label="Toggle availability">
            <span className={`toggle-label${available ? ' toggle-label--on' : ''}`}>
              {available ? 'Available to help' : "I'm unavailable to help"}
            </span>
            <span className={`toggle-track${available ? ' toggle-track--on' : ''}`}
                  onClick={() => setAvailable(v => !v)}
                  role="switch" aria-checked={available} tabIndex={0}
                  onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && setAvailable(v => !v)}>
              <span className="toggle-thumb" />
            </span>
          </label>
        </div>

        <form onSubmit={handleSubmit} className="helper-form">

          {/* ── Equipment ─────────────────────────────── */}
          <section className="profile-section">
            <h2>Equipment</h2>
            <p className="section-hint">Select everything you own that could help in an emergency.</p>
            <div className="chip-grid">
              {catalogLoading
                ? <p className="section-hint">Loading…</p>
                : equipmentCatalog.map(item => (
                  <button
                    key={item.id} type="button"
                    className={`chip${equipment.includes(item.id) ? ' selected' : ''}`}
                    onClick={() => toggleId(setEquipment, item.id)}
                  >
                    {item.label}
                  </button>
                ))
              }
            </div>
          </section>

          {/* ── Skills & Certifications ───────────────── */}
          <section className="profile-section">
            <h2>Skills &amp; Certifications</h2>
            <p className="section-hint">Select any skills or certifications you hold.</p>
            <div className="chip-grid">
              {catalogLoading
                ? <p className="section-hint">Loading…</p>
                : skillsCatalog.map(item => (
                  <button
                    key={item.id} type="button"
                    className={`chip${skills.includes(item.id) ? ' selected' : ''}`}
                    onClick={() => toggleId(setSkills, item.id)}
                  >
                    {item.label}
                  </button>
                ))
              }
            </div>
          </section>

          {/* ── Availability ──────────────────────────── */}
          <section className="profile-section">
            <h2>Availability</h2>
            <p className="section-hint">Tap a cell to mark when you're generally available to respond.</p>
            <div className="avail-scroll">
              <table className="avail-grid">
                <thead>
                  <tr>
                    <th className="time-col"></th>
                    {DAYS.map(d => <th key={d}>{d}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {TIME_BLOCKS.map(({ label, sub }) => (
                    <tr key={label}>
                      <td className="time-label">
                        <span className="time-name">{label}</span>
                        <span className="time-sub">{sub}</span>
                      </td>
                      {DAYS.map(d => (
                        <td key={d} className="avail-cell-wrap">
                          <button
                            type="button"
                            className={`avail-cell${availability[d][label] ? ' active' : ''}`}
                            onClick={() => toggleAvail(d, label)}
                            aria-label={`${d} ${label}`}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <div className="form-actions">
            <button type="submit" className="btn-save" disabled={isSaving}>
              {isSaving ? 'Saving…' : 'Save Profile'}
            </button>
            <button type="button" className="btn-skip" disabled={isSaving} onClick={() => navigate('/dashboard')}>
              Cancel
            </button>
          </div>

        </form>
      </div>
    </div>
  );
}

export default SetupHelper;

